"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet"
import { Menu } from "lucide-react"
import { Link, usePathname } from "@/i18n/navigation"
import { useTranslations } from "next-intl"

interface MobileMenuProps {
  navigationLinks: Array<{ name: string; href: string }>
}

export function MobileMenu({ navigationLinks }: MobileMenuProps) {
  const [open, setOpen] = useState(false)
  const t = useTranslations("nav")
  const pathname = usePathname()

  // Helper function to check if a link is active
  const isActiveLink = (href: string) => {
    if (href.startsWith("#")) return false // Anchor links are never active
    if (href === "/") return pathname === "/"
    return pathname.startsWith(href)
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          aria-label="Open menu"
        >
          <Menu className="h-6 w-6" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[300px] sm:w-[400px]">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="px-6 pt-6 pb-4">
            <h2 className="text-lg font-semibold">Menu</h2>
          </div>
          
              {/* Navigation Links */}
              <nav className="flex flex-col px-6 space-y-1 flex-1">
                {navigationLinks.map((link) => {
                  const isActive = isActiveLink(link.href)
                  const isAnchorLink = link.href.startsWith("#")
                  const className = `text-base font-medium py-3 px-2 rounded-md transition-colors ${
                    isActive
                      ? "text-accent font-semibold bg-accent/10"
                      : "text-foreground hover:text-foreground hover:bg-muted"
                  }`
                  
                  if (isAnchorLink) {
                    return (
                      <a
                        key={link.name}
                        href={link.href}
                        onClick={() => setOpen(false)}
                        className={className}
                      >
                        {link.name}
                      </a>
                    )
                  }
                  
                  return (
                    <Link
                      key={link.name}
                      href={link.href as "/" | "/about" | "/impressum" | "/privacy" | "/terms" | "/gtc" | "/beta-terms" | "/waitlist"}
                      onClick={() => setOpen(false)}
                      className={className}
                    >
                      {link.name}
                    </Link>
                  )
                })}
              </nav>
          
          {/* Action Buttons */}
          <div className="flex flex-col space-y-3 px-6 pt-4 pb-6 border-t">
            <Button
              variant="ghost"
              className="w-full justify-start py-3"
              onClick={() => setOpen(false)}
              asChild
            >
              <a href="https://app.clubpilot.at" target="_blank" rel="noopener noreferrer">
                {t("signIn")}
              </a>
            </Button>
            <Button
              className="w-full py-3"
              onClick={() => setOpen(false)}
              asChild
            >
              <Link href="/waitlist">
                {t("startFreeTrial")}
              </Link>
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}

